import React, { useEffect, useState } from 'react';
import { FaEye, FaPen, FaExchangeAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import {
  getRhEffectifs, getRhEmployes, getRhEmploye, createRhEmploye, updateRhEmploye,
  createAffectation, getRhPerformance, getStations
} from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const GLOBAL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'auditeur'];
const ALL_ROLES = ['superadmin', 'admin_siege', 'comptable', 'financier', 'fiscaliste', 'gerant', 'pompiste', 'auditeur'];
const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin', admin_siege: 'Admin Siege', comptable: 'Comptable',
  financier: 'Financier', fiscaliste: 'Fiscaliste', gerant: 'Gerant',
  pompiste: 'Pompiste', auditeur: 'Auditeur'
};
const ROLE_COLORS: Record<string, string> = {
  superadmin: '#ED0000', admin_siege: '#004A97', comptable: '#FF6B35',
  financier: '#78BE20', fiscaliste: '#9c27b0', gerant: '#2196f3',
  pompiste: '#ff9800', auditeur: '#607d8b'
};
const CHART_COLORS = ['#004A97', '#ED0000', '#FF6B35', '#78BE20', '#9c27b0', '#2196f3', '#ff9800', '#607d8b'];
const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA';

const RH: React.FC = () => {
  const { user } = useAuth();
  const isGlobal = GLOBAL_ROLES.includes(user?.role || '');
  const [tab, setTab] = useState('effectifs');
  const [stationFilter, setStationFilter] = useState('');
  const [stations, setStations] = useState<any[]>([]);

  // Effectifs
  const [effectifs, setEffectifs] = useState<any>(null);
  const [loadingEff, setLoadingEff] = useState(true);

  // Employes
  const [employes, setEmployes] = useState<any[]>([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('actif');

  // Detail employe
  const [selectedEmploye, setSelectedEmploye] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Form create/edit
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', telephone: '', role: 'pompiste', station_id: '', password: '',
    matricule: '', sexe: '', date_naissance: '', lieu_naissance: '', nationalite: 'Senegalaise',
    situation_familiale: 'celibataire', nb_enfants: '0', adresse: '', numero_cnss: '', numero_ipres: '',
    date_embauche: '', type_contrat: 'CDI', salaire_base: '', poste: '', categorie_professionnelle: '',
    is_cadre: false as boolean, banque: '', numero_compte_bancaire: ''
  });
  const [saving, setSaving] = useState(false);

  // Transfer
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferData, setTransferData] = useState({ user_id: 0, station_id: '', role_station: '' });

  // Performance
  const [performance, setPerformance] = useState<any[]>([]);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [perfMois, setPerfMois] = useState(String(new Date().getMonth() + 1));
  const [perfAnnee, setPerfAnnee] = useState(String(new Date().getFullYear()));

  useEffect(() => {
    if (isGlobal) getStations().then(r => setStations(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (tab === 'effectifs') loadEffectifs();
    if (tab === 'employes') loadEmployes();
    if (tab === 'performance') loadPerformance();
  }, [tab, stationFilter]);

  const loadEffectifs = async () => {
    setLoadingEff(true);
    try {
      const res = await getRhEffectifs();
      setEffectifs(res.data);
    } catch (err) { console.error(err); }
    setLoadingEff(false);
  };

  const loadEmployes = async () => {
    setLoadingEmp(true);
    try {
      const params: any = {};
      if (stationFilter) params.station_id = stationFilter;
      if (roleFilter) params.role = roleFilter;
      if (searchFilter) params.search = searchFilter;
      if (statutFilter) params.statut = statutFilter;
      const res = await getRhEmployes(params);
      setEmployes(res.data);
    } catch (err) { console.error(err); }
    setLoadingEmp(false);
  };

  const loadPerformance = async () => {
    setLoadingPerf(true);
    try {
      const params: any = { mois: perfMois, annee: perfAnnee };
      if (stationFilter) params.station_id = stationFilter;
      const res = await getRhPerformance(params);
      setPerformance(res.data);
    } catch (err) { console.error(err); }
    setLoadingPerf(false);
  };

  useEffect(() => {
    if (tab === 'employes') loadEmployes();
  }, [roleFilter, searchFilter, statutFilter]);

  useEffect(() => {
    if (tab === 'performance') loadPerformance();
  }, [perfMois, perfAnnee]);

  // Detail
  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setShowDetail(true);
    try {
      const res = await getRhEmploye(id);
      setSelectedEmploye(res.data);
    } catch (err) { console.error(err); }
    setDetailLoading(false);
  };

  // Form
  const openCreateForm = () => {
    setEditMode(false); setEditId(null);
    setFormData({
      nom: '', prenom: '', email: '', telephone: '', role: 'pompiste', station_id: stationFilter, password: '',
      matricule: '', sexe: '', date_naissance: '', lieu_naissance: '', nationalite: 'Senegalaise',
      situation_familiale: 'celibataire', nb_enfants: '0', adresse: '', numero_cnss: '', numero_ipres: '',
      date_embauche: '', type_contrat: 'CDI', salaire_base: '', poste: '', categorie_professionnelle: '',
      is_cadre: false, banque: '', numero_compte_bancaire: ''
    });
    setShowForm(true);
  };
  const openEditForm = (emp: any) => {
    setEditMode(true); setEditId(emp.id);
    setFormData({
      nom: emp.nom, prenom: emp.prenom, email: emp.email, telephone: emp.telephone || '',
      role: emp.role, station_id: String(emp.station_id || ''), password: '',
      matricule: emp.matricule || '', sexe: emp.sexe || '', date_naissance: emp.date_naissance || '',
      lieu_naissance: emp.lieu_naissance || '', nationalite: emp.nationalite || 'Senegalaise',
      situation_familiale: emp.situation_familiale || 'celibataire', nb_enfants: String(emp.nb_enfants ?? '0'),
      adresse: emp.adresse || '', numero_cnss: emp.numero_cnss || '', numero_ipres: emp.numero_ipres || '',
      date_embauche: emp.date_embauche || '', type_contrat: emp.type_contrat || 'CDI',
      salaire_base: String(emp.salaire_base ?? ''), poste: emp.poste || '',
      categorie_professionnelle: emp.categorie_professionnelle || '',
      is_cadre: !!emp.is_cadre, banque: emp.banque || '', numero_compte_bancaire: emp.numero_compte_bancaire || ''
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.nom || !formData.prenom || !formData.email) return alert('Nom, prenom et email sont requis');
    setSaving(true);
    try {
      const data: any = {
        ...formData,
        station_id: formData.station_id ? Number(formData.station_id) : null,
        is_cadre: formData.is_cadre ? 1 : 0,
        salaire_base: formData.salaire_base ? Number(formData.salaire_base) : null,
        nb_enfants: formData.nb_enfants ? Number(formData.nb_enfants) : 0
      };
      if (!data.password) delete data.password;
      if (editMode && editId) {
        await updateRhEmploye(editId, data);
      } else {
        await createRhEmploye(data);
      }
      setShowForm(false);
      loadEmployes();
      if (tab === 'effectifs') loadEffectifs();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  // Transfer
  const openTransfer = (emp: any) => {
    setTransferData({ user_id: emp.id, station_id: '', role_station: emp.role });
    setShowTransfer(true);
  };
  const handleTransfer = async () => {
    if (!transferData.station_id) return alert('Selectionnez une station');
    setSaving(true);
    try {
      await createAffectation(transferData);
      setShowTransfer(false);
      loadEmployes();
      if (showDetail && selectedEmploye) {
        const res = await getRhEmploye(selectedEmploye.employe.id);
        setSelectedEmploye(res.data);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
    setSaving(false);
  };

  const tabs = [
    { key: 'effectifs', label: 'Vue d\'ensemble' },
    { key: 'employes', label: 'Employes' },
    { key: 'performance', label: 'Performance Pompistes' },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>Ressources Humaines</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isGlobal && (
            <select value={stationFilter} onChange={e => setStationFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Toutes les stations</option>
              {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          {tab === 'employes' && (
            <button className="btn btn-primary" onClick={openCreateForm}>+ Nouvel Employe</button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #eee', marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: tab === t.key ? '3px solid #ED0000' : '3px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#ED0000' : '#666', fontSize: 14
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════ TAB: VUE D'ENSEMBLE ═══════ */}
      {tab === 'effectifs' && (
        loadingEff ? <div className="loading">Chargement...</div> : effectifs && (
          <>
            {/* KPIs */}
            <div className="stats-grid">
              <div className="stat-card" style={{ borderTop: '4px solid #004A97' }}>
                <div className="stat-value">{effectifs.total}</div>
                <div className="stat-label">TOTAL PERSONNEL</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #78BE20' }}>
                <div className="stat-value">{effectifs.actifs}</div>
                <div className="stat-label">ACTIFS</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #ED0000' }}>
                <div className="stat-value">{effectifs.inactifs}</div>
                <div className="stat-label">INACTIFS</div>
              </div>
              <div className="stat-card" style={{ borderTop: '4px solid #FF6B35' }}>
                <div className="stat-value">{effectifs.recrutements_recents?.length || 0}</div>
                <div className="stat-label">RECRUTES (30J)</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
              {/* Repartition par role */}
              <div className="card">
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Repartition par role</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={effectifs.parRole?.map((r: any) => ({ name: ROLE_LABELS[r.role] || r.role, value: r.total }))}
                      cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }: any) => `${name}: ${value}`}>
                      {effectifs.parRole?.map((_: any, i: number) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Effectif par station */}
              <div className="card">
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Effectif par station</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={effectifs.parStation?.filter((s: any) => s.nom)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="nom" type="category" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total" fill="#004A97" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recrutements recents */}
            {effectifs.recrutements_recents?.length > 0 && (
              <div className="card" style={{ marginTop: 20 }}>
                <h3 style={{ color: '#004A97', marginBottom: 16 }}>Recrutements recents (30 derniers jours)</h3>
                <table className="table">
                  <thead>
                    <tr><th>Nom</th><th>Role</th><th>Station</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {effectifs.recrutements_recents.map((r: any) => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 600 }}>{r.prenom} {r.nom}</td>
                        <td><span className="badge" style={{ background: ROLE_COLORS[r.role] || '#666', color: '#fff' }}>{ROLE_LABELS[r.role] || r.role}</span></td>
                        <td>{r.station_nom || '-'}</td>
                        <td>{new Date(r.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}

      {/* ═══════ TAB: EMPLOYES ═══════ */}
      {tab === 'employes' && (
        <>
          <div className="filters" style={{ marginBottom: 16 }}>
            <input type="text" placeholder="Rechercher..." value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', width: 220 }} />
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Tous les roles</option>
              {ALL_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <select value={statutFilter} onChange={e => setStatutFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              <option value="">Tous</option>
              <option value="actif">Actifs</option>
              <option value="inactif">Inactifs</option>
            </select>
            <span className="badge">{employes.length} employes</span>
          </div>

          {loadingEmp ? <div className="loading">Chargement...</div> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th><th>Email</th><th>Telephone</th><th>Role</th>
                    <th>Poste</th><th style={{ textAlign: 'right' }}>Salaire Base</th>
                    <th>Station</th>
                    <th style={{ textAlign: 'right' }}>Releves</th>
                    <th>Derniere activite</th><th>Statut</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employes.length === 0 ? (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun employe trouve</td></tr>
                  ) : employes.map((e: any) => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600 }}>{e.prenom} {e.nom}</td>
                      <td style={{ fontSize: 13, color: '#666' }}>{e.email}</td>
                      <td>{e.telephone || '-'}</td>
                      <td>
                        <span className="badge" style={{ background: ROLE_COLORS[e.role] || '#666', color: '#fff', fontSize: 11 }}>
                          {ROLE_LABELS[e.role] || e.role}
                        </span>
                      </td>
                      <td>{e.poste || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{e.salaire_base ? formatMontant(Number(e.salaire_base)) : '-'}</td>
                      <td>{e.station_nom || <span style={{ color: '#999' }}>Siege</span>}</td>
                      <td style={{ textAlign: 'right' }}>{e.nb_releves || 0}</td>
                      <td style={{ fontSize: 13 }}>
                        {e.derniere_activite ? new Date(e.derniere_activite).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td>
                        <span className={`badge badge-${e.etat ? 'success' : 'danger'}`}>
                          {e.etat ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm" onClick={() => openDetail(e.id)} title="Details"><FaEye /></button>
                          <button className="btn btn-sm" onClick={() => openEditForm(e)} title="Modifier"><FaPen /></button>
                          {e.station_id && <button className="btn btn-sm" onClick={() => openTransfer(e)} title="Transferer"><FaExchangeAlt /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB: PERFORMANCE POMPISTES ═══════ */}
      {tab === 'performance' && (
        <>
          <div className="filters" style={{ marginBottom: 16 }}>
            <select value={perfMois} onChange={e => setPerfMois(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              {['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'].map((m, i) =>
                <option key={i+1} value={i+1}>{m}</option>
              )}
            </select>
            <select value={perfAnnee} onChange={e => setPerfAnnee(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="badge">{performance.length} pompistes</span>
          </div>

          {loadingPerf ? <div className="loading">Chargement...</div> : (
            <>
              {/* Top 10 chart */}
              {performance.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3 style={{ color: '#004A97', marginBottom: 16 }}>Top Pompistes par Volume</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={performance.slice(0, 10).map((p: any) => ({ nom: `${p.prenom} ${p.nom}`, volume: Number(p.total_volume) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="nom" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
                      <YAxis />
                      <Tooltip formatter={(v: any) => `${Number(v).toLocaleString()} L`} />
                      <Bar dataKey="volume" fill="#004A97" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th><th>Pompiste</th><th>Station</th>
                      <th style={{ textAlign: 'right' }}>Releves</th>
                      <th style={{ textAlign: 'right' }}>Volume Total</th>
                      <th style={{ textAlign: 'right' }}>CA Total</th>
                      <th style={{ textAlign: 'right' }}>Ecart Total</th>
                      <th style={{ textAlign: 'right' }}>Ecart Moy %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.length === 0 ? (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune donnee pour cette periode</td></tr>
                    ) : performance.map((p: any, i: number) => (
                      <tr key={p.id} style={{ background: i < 3 ? '#f0f8ff' : undefined }}>
                        <td>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span style={{ color: '#999' }}>{i + 1}</span>}
                        </td>
                        <td style={{ fontWeight: 600 }}>{p.prenom} {p.nom}</td>
                        <td>{p.station_nom}</td>
                        <td style={{ textAlign: 'right' }}>{p.nb_releves}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(p.total_volume).toLocaleString()} L</td>
                        <td style={{ textAlign: 'right' }}>{formatMontant(Number(p.total_montant))}</td>
                        <td style={{ textAlign: 'right', color: Math.abs(p.total_ecart) > 1000 ? '#e53935' : '#4caf50' }}>
                          {Number(p.total_ecart) > 0 ? '+' : ''}{formatMontant(Number(p.total_ecart))}
                        </td>
                        <td style={{ textAlign: 'right', color: Math.abs(p.avg_ecart_pourcent) > 1 ? '#e53935' : '#4caf50' }}>
                          {p.avg_ecart_pourcent > 0 ? '+' : ''}{p.avg_ecart_pourcent}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ═══════ MODAL: DETAIL EMPLOYE ═══════ */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedEmploye ? `${selectedEmploye.employe.prenom} ${selectedEmploye.employe.nom}` : 'Chargement...'}</h3>
              <button className="modal-close" onClick={() => setShowDetail(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {detailLoading ? <div className="loading">Chargement...</div> : selectedEmploye && (
                <>
                  {/* Infos */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div className="recap-card">
                      <span className="recap-label">Role</span>
                      <span className="badge" style={{ background: ROLE_COLORS[selectedEmploye.employe.role] || '#666', color: '#fff' }}>
                        {ROLE_LABELS[selectedEmploye.employe.role]}
                      </span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Station</span>
                      <span style={{ fontWeight: 700 }}>{selectedEmploye.employe.station_nom || 'Siege'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Email</span>
                      <span style={{ fontSize: 13 }}>{selectedEmploye.employe.email}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Telephone</span>
                      <span>{selectedEmploye.employe.telephone || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Statut</span>
                      <span className={`badge badge-${selectedEmploye.employe.etat ? 'success' : 'danger'}`}>
                        {selectedEmploye.employe.etat ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Date d'inscription</span>
                      <span style={{ fontSize: 13 }}>{new Date(selectedEmploye.employe.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>

                  {/* Informations complementaires */}
                  <h4 style={{ color: '#004A97', marginTop: 16, marginBottom: 8 }}>Informations personnelles</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div className="recap-card">
                      <span className="recap-label">Matricule</span>
                      <span>{selectedEmploye.employe.matricule || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Date de naissance</span>
                      <span>{selectedEmploye.employe.date_naissance ? new Date(selectedEmploye.employe.date_naissance).toLocaleDateString('fr-FR') : '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Lieu de naissance</span>
                      <span>{selectedEmploye.employe.lieu_naissance || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Nationalite</span>
                      <span>{selectedEmploye.employe.nationalite || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Sexe</span>
                      <span>{selectedEmploye.employe.sexe === 'M' ? 'Masculin' : selectedEmploye.employe.sexe === 'F' ? 'Feminin' : '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Situation familiale</span>
                      <span style={{ textTransform: 'capitalize' }}>{selectedEmploye.employe.situation_familiale || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Nb enfants</span>
                      <span>{selectedEmploye.employe.nb_enfants ?? '-'}</span>
                    </div>
                  </div>

                  <h4 style={{ color: '#004A97', marginTop: 16, marginBottom: 8 }}>Informations professionnelles</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div className="recap-card">
                      <span className="recap-label">Poste</span>
                      <span>{selectedEmploye.employe.poste || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Categorie professionnelle</span>
                      <span>{selectedEmploye.employe.categorie_professionnelle || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Type contrat</span>
                      <span>{selectedEmploye.employe.type_contrat || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Date d'embauche</span>
                      <span>{selectedEmploye.employe.date_embauche ? new Date(selectedEmploye.employe.date_embauche).toLocaleDateString('fr-FR') : '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Salaire de base</span>
                      <span style={{ fontWeight: 700 }}>{selectedEmploye.employe.salaire_base ? formatMontant(Number(selectedEmploye.employe.salaire_base)) : '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Cadre</span>
                      <span>{selectedEmploye.employe.is_cadre ? 'Oui' : 'Non'}</span>
                    </div>
                  </div>

                  <h4 style={{ color: '#004A97', marginTop: 16, marginBottom: 8 }}>Securite sociale & Banque</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div className="recap-card">
                      <span className="recap-label">Numero CNSS</span>
                      <span>{selectedEmploye.employe.numero_cnss || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Numero IPRES</span>
                      <span>{selectedEmploye.employe.numero_ipres || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Banque</span>
                      <span>{selectedEmploye.employe.banque || '-'}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Numero compte bancaire</span>
                      <span>{selectedEmploye.employe.numero_compte_bancaire || '-'}</span>
                    </div>
                  </div>

                  {/* Performance globale */}
                  <h4 style={{ color: '#004A97', marginBottom: 12 }}>Performance globale</h4>
                  <div className="journal-recap">
                    <div className="recap-card recap-green">
                      <span className="recap-label">Total Releves</span>
                      <span className="recap-value">{selectedEmploye.performance?.total_releves || 0}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Volume Total</span>
                      <span className="recap-value">{Number(selectedEmploye.performance?.total_volume || 0).toLocaleString()} L</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">CA Total</span>
                      <span className="recap-value">{formatMontant(Number(selectedEmploye.performance?.total_montant || 0))}</span>
                    </div>
                    <div className={`recap-card ${Math.abs(selectedEmploye.performance?.total_ecart || 0) > 5000 ? 'recap-red' : 'recap-green'}`}>
                      <span className="recap-label">Ecart Total</span>
                      <span className="recap-value">{formatMontant(Number(selectedEmploye.performance?.total_ecart || 0))}</span>
                    </div>
                    <div className="recap-card">
                      <span className="recap-label">Ecart Moyen</span>
                      <span className="recap-value">{Number(selectedEmploye.performance?.ecart_moyen || 0).toFixed(2)}%</span>
                    </div>
                  </div>

                  {/* Activite mensuelle */}
                  {selectedEmploye.activite_mensuelle?.length > 0 && (
                    <>
                      <h4 style={{ color: '#004A97', margin: '20px 0 12px' }}>Activite mensuelle (6 derniers mois)</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={selectedEmploye.activite_mensuelle.reverse()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
                          <YAxis />
                          <Tooltip formatter={(v: any) => `${Number(v).toLocaleString()} L`} />
                          <Bar dataKey="volume" fill="#004A97" radius={[4, 4, 0, 0]} name="Volume (L)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  )}

                  {/* Historique affectations */}
                  {selectedEmploye.affectations?.length > 0 && (
                    <>
                      <h4 style={{ color: '#004A97', margin: '20px 0 12px' }}>Historique des affectations</h4>
                      <table className="table">
                        <thead>
                          <tr><th>Station</th><th>Role</th><th>Date</th><th>Statut</th></tr>
                        </thead>
                        <tbody>
                          {selectedEmploye.affectations.map((a: any) => (
                            <tr key={a.id}>
                              <td style={{ fontWeight: 600 }}>{a.station_nom} <code style={{ fontSize: 11 }}>{a.station_code}</code></td>
                              <td>{ROLE_LABELS[a.role_station] || a.role_station}</td>
                              <td>{a.date_affectation ? new Date(a.date_affectation).toLocaleDateString('fr-FR') : '-'}</td>
                              <td><span className={`badge badge-${a.actif ? 'success' : 'warning'}`}>{a.actif ? 'Active' : 'Terminee'}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {selectedEmploye && (
                <>
                  <button className="btn btn-warning" onClick={() => { setShowDetail(false); openEditForm(selectedEmploye.employe); }}><FaPen style={{ marginRight: 6 }} />Modifier</button>
                  <button className="btn btn-primary" onClick={() => openTransfer(selectedEmploye.employe)}><FaExchangeAlt style={{ marginRight: 6 }} />Transferer</button>
                </>
              )}
              <button className="btn" onClick={() => setShowDetail(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ MODAL: FORMULAIRE EMPLOYE ═══════ */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Modifier l\'employe' : 'Nouvel employe'}</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <h4 style={{ color: '#004A97', marginTop: 0, marginBottom: 8 }}>Informations personnelles</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Prenom *</label>
                  <input type="text" value={formData.prenom} onChange={e => setFormData(p => ({ ...p, prenom: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input type="text" value={formData.nom} onChange={e => setFormData(p => ({ ...p, nom: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Telephone</label>
                  <input type="text" value={formData.telephone} onChange={e => setFormData(p => ({ ...p, telephone: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Matricule</label>
                  <input type="text" value={formData.matricule} onChange={e => setFormData(p => ({ ...p, matricule: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Sexe</label>
                  <select value={formData.sexe} onChange={e => setFormData(p => ({ ...p, sexe: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date de naissance</label>
                  <input type="date" value={formData.date_naissance} onChange={e => setFormData(p => ({ ...p, date_naissance: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Lieu de naissance</label>
                  <input type="text" value={formData.lieu_naissance} onChange={e => setFormData(p => ({ ...p, lieu_naissance: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Nationalite</label>
                  <input type="text" value={formData.nationalite} onChange={e => setFormData(p => ({ ...p, nationalite: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Situation familiale</label>
                  <select value={formData.situation_familiale} onChange={e => setFormData(p => ({ ...p, situation_familiale: e.target.value }))}>
                    <option value="celibataire">Celibataire</option>
                    <option value="marie">Marie(e)</option>
                    <option value="divorce">Divorce(e)</option>
                    <option value="veuf">Veuf(ve)</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Nb enfants</label>
                  <input type="number" min="0" value={formData.nb_enfants} onChange={e => setFormData(p => ({ ...p, nb_enfants: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Adresse</label>
                  <input type="text" value={formData.adresse} onChange={e => setFormData(p => ({ ...p, adresse: e.target.value }))} />
                </div>
              </div>

              <h4 style={{ color: '#004A97', marginTop: 16, marginBottom: 8 }}>Informations professionnelles</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Role *</label>
                  <select value={formData.role} onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}>
                    {ALL_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Station</label>
                  <select value={formData.station_id} onChange={e => setFormData(p => ({ ...p, station_id: e.target.value }))}>
                    <option value="">-- Siege (sans station) --</option>
                    {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom} - {s.ville}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Poste</label>
                  <input type="text" value={formData.poste} onChange={e => setFormData(p => ({ ...p, poste: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Categorie professionnelle</label>
                  <input type="text" value={formData.categorie_professionnelle} onChange={e => setFormData(p => ({ ...p, categorie_professionnelle: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type contrat</label>
                  <select value={formData.type_contrat} onChange={e => setFormData(p => ({ ...p, type_contrat: e.target.value }))}>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="Stage">Stage</option>
                    <option value="Interim">Interim</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date d'embauche</label>
                  <input type="date" value={formData.date_embauche} onChange={e => setFormData(p => ({ ...p, date_embauche: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Salaire de base (FCFA)</label>
                  <input type="number" min="0" value={formData.salaire_base} onChange={e => setFormData(p => ({ ...p, salaire_base: e.target.value }))} />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 24 }}>
                  <input type="checkbox" checked={formData.is_cadre} onChange={e => setFormData(p => ({ ...p, is_cadre: e.target.checked }))}
                    style={{ width: 18, height: 18 }} />
                  <label style={{ margin: 0 }}>Cadre</label>
                </div>
              </div>

              <h4 style={{ color: '#004A97', marginTop: 16, marginBottom: 8 }}>Securite sociale & Banque</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Numero CNSS</label>
                  <input type="text" value={formData.numero_cnss} onChange={e => setFormData(p => ({ ...p, numero_cnss: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Numero IPRES</label>
                  <input type="text" value={formData.numero_ipres} onChange={e => setFormData(p => ({ ...p, numero_ipres: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Banque</label>
                  <input type="text" value={formData.banque} onChange={e => setFormData(p => ({ ...p, banque: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Numero compte bancaire</label>
                  <input type="text" value={formData.numero_compte_bancaire} onChange={e => setFormData(p => ({ ...p, numero_compte_bancaire: e.target.value }))} />
                </div>
              </div>

              {!editMode && (
                <>
                  <h4 style={{ color: '#004A97', marginTop: 16, marginBottom: 8 }}>Acces</h4>
                  <div className="form-group">
                    <label>Mot de passe (defaut: Pass@2026)</label>
                    <input type="password" value={formData.password} placeholder="Pass@2026"
                      onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowForm(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement...' : (editMode ? 'Enregistrer' : 'Creer l\'employe')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ MODAL: TRANSFERT ═══════ */}
      {showTransfer && (
        <div className="modal-overlay" onClick={() => setShowTransfer(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transferer un employe</h3>
              <button className="modal-close" onClick={() => setShowTransfer(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nouvelle station *</label>
                <select value={transferData.station_id} onChange={e => setTransferData(p => ({ ...p, station_id: e.target.value }))}>
                  <option value="">-- Selectionner --</option>
                  {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom} - {s.ville}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Role a la station</label>
                <select value={transferData.role_station} onChange={e => setTransferData(p => ({ ...p, role_station: e.target.value }))}>
                  {ALL_ROLES.filter(r => ['gerant', 'pompiste'].includes(r)).map(r =>
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  )}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowTransfer(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleTransfer} disabled={saving}>
                {saving ? 'Transfert...' : 'Confirmer le transfert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RH;
